import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Divider,
  Paper,
  Stack,
  Avatar,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ContactPhone as ContactPhoneIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { contractorsApi } from '../../services/api';

const CreateContractorModal = ({ open, onClose, onCompanyCreated }) => {
  const { user, getUserOrganization } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    license_number: '',
    registration_number: '',
    is_active: true,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const organizationInfo = getUserOrganization();
      const companyData = {
        ...formData,
        organization_id: organizationInfo.id
      };

      await contractorsApi.createCompany(companyData);
      
      setFormData({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        license_number: '',
        registration_number: '',
        is_active: true,
        notes: ''
      });
      
      onCompanyCreated();
    } catch (error) {
      console.error('Failed to create contractor company:', error);
      setError(error.response?.data?.message || 'Failed to create contractor company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        license_number: '',
        registration_number: '',
        is_active: true,
        notes: ''
      });
      setError('');
      onClose();
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          height: '95vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          m: 1
        }
      }}
    >
      {/* Sticky Header */}
      <DialogTitle 
        sx={{ 
          p: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <AddIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  Add New Contractor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a new contractor company profile
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Quick Info Card */}
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2,
              p: 2,
              bgcolor: 'info.main',
              color: 'info.contrastText',
              borderRadius: 2
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'info.dark' }}>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  External Service Provider
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage contractor companies for outsourced work assignments
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent 
        sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}
      >
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {/* Basic Information Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <BusinessIcon color="primary" />
                  Basic Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="name"
                      label="Company Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      placeholder="e.g., Acme Road Repair Inc."
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="contact_person"
                      label="Contact Person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="Primary contact name"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="contact@company.com"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="phone"
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="+1 (555) 123-4567"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="address"
                      label="Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Company physical address"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Registration Information Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <DescriptionIcon color="primary" />
                  Registration Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="license_number"
                      label="License Number"
                      value={formData.license_number}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="Professional license number"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="registration_number"
                      label="Registration Number"
                      value={formData.registration_number}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="Business registration number"
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Additional Information Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <ContactPhoneIcon color="primary" />
                  Additional Information
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    name="notes"
                    label="Notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Additional notes about the contractor company"
                  />

                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'grey.300'
                  }}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Active Company
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable this contractor for work assignments
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </form>
        </Box>
      </DialogContent>

      {/* Sticky Footer */}
      <DialogActions 
        sx={{ 
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 3
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading}
          size="large"
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained" 
          disabled={loading}
          size="large"
          sx={{ minWidth: 160 }}
        >
          {loading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={20} color="inherit" />
              <Typography>Creating...</Typography>
            </Stack>
          ) : (
            'Create Company'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateContractorModal; 